import React, { useRef, useCallback } from 'react';
import { useClickOutside } from './useClickOutside';

export type AttachmentMenuItemId = 'attachments' | 'templates' | 'ai-suggestions' | 'quick-replies';

export interface AttachmentMenuProps {
  isOpen: boolean;
  onToggle: () => void;
  onItemSelect: (itemId: AttachmentMenuItemId) => void;
  disabled?: boolean;
}

const menuItems: { id: AttachmentMenuItemId; label: string; color: string; icon: React.ReactNode }[] = [
  {
    id: 'attachments',
    label: 'Attachments',
    color: '#7f66ff',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
      </svg>
    ),
  },
  {
    id: 'templates',
    label: 'Templates',
    color: '#00a884',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
  },
  {
    id: 'ai-suggestions',
    label: 'AI Suggestions',
    color: '#8b5cf6',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
    ),
  },
  {
    id: 'quick-replies',
    label: 'Quick Replies',
    color: '#f59e0b',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
      </svg>
    ),
  },
];

const AttachmentMenu: React.FC<AttachmentMenuProps> = ({ isOpen, onToggle, onItemSelect, disabled }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const handleClose = useCallback(() => {
    if (isOpen) onToggle();
  }, [isOpen, onToggle]);

  useClickOutside(containerRef, handleClose, isOpen);

  return (
    <div className="relative flex-shrink-0" ref={containerRef}>
      {/* Popup */}
      {isOpen && (
        <div className="absolute bottom-full left-0 mb-2 bg-warm-white rounded-xl shadow-lg p-4 animate-attachment-menu z-50">
          <div className="grid grid-cols-4 gap-3" style={{ minWidth: '280px' }}>
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => onItemSelect(item.id)}
                className="flex flex-col items-center gap-1.5 group"
              >
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center text-warm-white transition-transform group-active:scale-90"
                  style={{ backgroundColor: item.color }}
                >
                  {item.icon}
                </div>
                <span className="text-[11px] text-graphite leading-tight">{item.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* "+" Toggle Button */}
      <button
        onClick={onToggle}
        disabled={disabled}
        className="w-[42px] h-[42px] rounded-full flex items-center justify-center text-[#8696a0] hover:text-charcoal hover:bg-charcoal/10 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        title="Attach"
      >
        <svg
          className="w-6 h-6 transition-transform duration-200"
          style={{ transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)' }}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      </button>
    </div>
  );
};

export { AttachmentMenu };
